import type {
  MutationFunction,
  UseMutationOptions,
} from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { showApiError, useMutation } from '@fv/client-core'

type Options<TData, TError, TVariables, TContext> = UseMutationOptions<
  TData,
  TError,
  TVariables,
  TContext
> & {
  toastErrorPrefix?: (
    data: TData | undefined,
    error: TError | null,
    vars: TVariables | undefined,
    context: TContext | undefined,
  ) => string
  toastSuccess?: (
    data: TData | undefined,
    error: TError | null,
    vars: TVariables | undefined,
    context: TContext | undefined,
  ) => string
}
export function useToastedMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: Omit<Options<TData, TError, TVariables, TContext>, 'mutationFn'>,
) {
  return useMutation({
    ...options,
    mutationFn,
    onSettled(...args) {
      const [data, error, vars, onMutateResult] = args
      if (error && options?.toastErrorPrefix) {
        showApiError(
          options.toastErrorPrefix(data, error, vars, onMutateResult),
          error,
        )
      } else if (!error && options?.toastSuccess) {
        toast.success(options.toastSuccess(data, error, vars, onMutateResult))
      }
      options?.onSettled?.apply(null, args)
    },
  })
}
